import postHog from "posthog-js";
import { omit } from "lodash";

import config from "utilities/dist/config";

export const initializeTrackingForAnalytics = () => {
    if (config?.posthog?.api_key) {
        postHog.init(config.posthog.api_key, { api_host: "https://app.posthog.com" });
    }
};

export const setUserDetailsForAnalytics = (user = {}) => {
    if (config?.posthog?.api_key && user?.id) {
        postHog.identify(user.id, omit(user, ["id"]));
    }
};

export const trackEventForAnalytics = (data = {}) => {
    if (config?.posthog?.api_key) {
        postHog.capture(data.category, omit(data, ["category"]));
    }
};

export const trackSaveStampRollEvent = () => {
    trackEventForAnalytics({
        category: "stamp_roll",
        action: "created new stamp roll",
    });
};

export const trackSaveStampAssignmentEvent = (options = {}) => {
    let action = `manually assigning stamps`;
    if (options.success) action = "manually assigned stamps";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        category: "stamp_roll_assignment",
        action,
    });
};

export const trackDownloadPdfEvent = (number = "", options = {}) => {
    let action = `creating pdf of invoice`;
    if (options.success) action = "pdf created for invoice";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: number,
        category: "invoice_pdf",
    });
};

export const trackDownloadCustomerBalancePdfEvent = (number = "", options = {}) => {
    let action = `creating pdf of customer_balance`;
    if (options.success) action = "pdf created for customer_balance";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: number,
        category: "customer_balance_pdf",
    });
};

export const trackInvoiceCreateEvent = (invoice = {}, options = {}) => {
    let action = `creating new invoice`;
    if (options.success) action = "new invoice created";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: invoice.id,
        category: "invoice_create",
    });
};

export const trackInvoiceEditEvent = (invoice = {}, options = {}) => {
    let action = `updating existing invoice`;
    if (options.success) action = "existing invoice updated";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: invoice.id,
        category: "invoice_update",
    });
};

export const trackCustomerCreateEvent = (customer = {}, options = {}) => {
    let action = `saving ${customer.id ? "existing" : "new"} customer`;
    if (options.success)
        action = `${customer.id ? "existing customer updated" : "new customer saved"}`;
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: customer.id,
        category: `${customer.id ? "customer_update" : "customer_create"}`,
    });
};

export const trackCustomerRemoveEvent = (customerId, options = {}) => {
    let action = `removing customer`;
    if (options.success) action = "customer removed";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: customerId,
        category: "customer_remove",
    });
};

export const trackProductCreateEvent = (product = {}, options = {}) => {
    let action = `creating new product`;
    if (options.success) action = "new product created";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: product.id,
        category: "product_create",
    });
};

export const trackSendEmailEvent = (emails = {}, options = {}) => {
    let action = `sending email`;
    if (options.success) action = "email has been sent";
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: emails.id,
        category: "email",
    });
};

export const trackActivateFeatureEvent = (confirming = {}, options = {}, isActive) => {
    let action = `activating ${confirming.id}`;
    if (options.success) action = `${confirming.id} is ${!isActive ? "activated" : "deactivated"}`;
    if (options.error) action = `error ${action}`;

    trackEventForAnalytics({
        action,
        value: confirming.id,
        category: "feature_activation",
    });
};

export const trackProductSelection = (product, inputQuery) => {
    trackEventForAnalytics({
        action: "product selected",
        value: product.id,
        category: "product_search",
        data: { query: inputQuery, product },
    });
};

export const trackProductAddRemoveToInvoice = (action, category, product, item) => {
    trackEventForAnalytics({
        action,
        value: product.id,
        category,
        data: {
            product,
            item,
        },
    });
};

export const trackAddRemoveInvoicePayment = (action, category, payment) => {
    trackEventForAnalytics({
        action,
        value: payment.id,
        category,
        data: payment,
    });
};

export const trackPasswordResetRequest = email => {
    trackEventForAnalytics({
        action: "password reset request",
        category: "password_reset_request",
        value: {
            email,
        },
    });
};

export const trackPasswordChangeFailure = error => {
    trackEventForAnalytics({
        action: "password change fail",
        category: "password_change_fail",
        value: {
            error,
        },
    });
};

export const trackInventoryRestock = (options = {}) => {
    let action = `restock`;
    if (options.populateList) action = `populate ${action} list`;

    trackEventForAnalytics({
        action,
        category: "inventory",
        value: options.itemCount,
    });
};
